import { Hub } from 'aws-amplify/utils'
import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Sends the user back to the login page when their Cognito session can no longer be renewed.
 *
 * Each tenant's app client is created with a 6-hour refresh token (facility-setup), and Cognito
 * does not reissue one on refresh, so it is an absolute cap from sign-in rather than an idle
 * timeout. When it runs out the refresh returns 400, Amplify drops its tokens, and every call
 * fails with `NoValidAuthTokens` — leaving the app rendered but dead unless something reacts.
 *
 * Gen1 got this from `<authenticator>` wrapping the whole app: the Amplify UI Hub handler signs
 * out on this event. Gen2 mounts the authenticator on /login only, so the behaviour is restored
 * here instead. Navigation is already covered by authGuard; this is for the idle case.
 */

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.'

export interface SessionExpiryDeps {
	authStore: Pick<ReturnType<typeof useAuthStore>, 'user' | 'error'>
	router: { push: (to: string) => unknown }
}

export function handleTokenRefreshFailure(
	error: Error | undefined,
	{ authStore, router }: SessionExpiryDeps,
): void {
	// Amplify keeps the tokens on a network error, so the session is still good. Signing out
	// here would eject a valid user over a wifi blip or a laptop waking up.
	if (error?.name === 'NetworkError') return

	// Already handled — concurrent requests each trigger their own refresh, so the event
	// arrives in a burst — or a cold boot, where nothing was signed in and authGuard redirects
	// without needing to announce an expiry.
	if (!authStore.user) return

	// Token storage is already clear (Amplify does it before dispatching). Nulling the user is
	// what flips isAuthenticated, and it is load-bearing: without it guestOnlyGuard bounces the
	// push below straight back to /home.
	authStore.user = null
	authStore.error = SESSION_EXPIRED_MESSAGE
	router.push('/login')
}

export function installSessionExpiryHandler(router: Router): void {
	Hub.listen('auth', ({ payload }) => {
		if (payload.event !== 'tokenRefresh_failure') return

		const error = (payload as { data?: { error?: Error } }).data?.error
		handleTokenRefreshFailure(error, { authStore: useAuthStore(), router })
	})
}
